/**
 * While placing (game/build/placement.ts): the Place button at the bottom, on where its preview can
 * stand, saying why not where it can't ("Must be placed on ore"), and × to stop placing.
 */
import type { JSX } from 'react';

import type { Blueprint } from '@game/build/blueprints';
import { confirmPlacing, placementCheck, stopPlacing } from '@game/build/placement';
import { reasonText, t } from '@i18n/index';
import { haptic } from '@platform/haptics';
import { ActionFace } from '@ui/components/ActionFace';
import type { ActionLook } from '@ui/components/actionLook';
import { useGame } from '@ui/hooks';
import { Icon } from '@ui/icons';

import { blueprintArt, blueprintName } from '../build/blueprintText';

import './placement.css';

export function PlacementBar({ blueprint }: { blueprint: Blueprint }): JSX.Element {
  const game = useGame();
  const placement = placementCheck(game);
  const name = blueprintName(blueprint);
  const state = placement?.state ?? 'waiting';
  const sub =
    placement?.state === 'refused'
      ? reasonText(placement.refusal)
      : state === 'waiting'
        ? t('reason.waiting')
        : name;
  const look: ActionLook = {
    label: t('place.confirm'),
    sub,
    aria:
      state === 'ok'
        ? t('place.confirm-aria', { name: name.toLowerCase() })
        : `${t('place.confirm')}: ${sub}`,
    art: { ...blueprintArt(blueprint), hammer: true },
    enabled: state === 'ok',
  };
  return (
    <div className="placing" data-state={state}>
      <button
        type="button"
        id="place"
        className="act place-btn"
        aria-disabled={!look.enabled}
        aria-label={look.aria}
        onClick={() => {
          if (confirmPlacing(game)) haptic('build');
          else haptic('refused');
        }}
      >
        <ActionFace look={look} />
      </button>
      <button
        type="button"
        className="cancel glass"
        aria-label={t('place.cancel')}
        onClick={() => stopPlacing(game)}
      >
        <Icon id="x" />
      </button>
    </div>
  );
}
